import { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { showAlert } from 'actions/app';
import HelloSign from 'hellosign-embedded';
import strings from 'strings';
import { Endpoints } from 'UI/constants/endpoints';
import { getErrorMessage } from 'UI/utils';

import API from './API';

const BASE_ALERT = {
  isNotification: false,
  isOpen: false,
  onClick: undefined,
  onDismissClick: undefined
};

const AlertTitle = strings.feeAgreements.singular;

/**
 * @param {number} feeId
 * @returns {Promise<OperationResult>}
 */
const sendReminder = async feeId => {
  const result = await sendRequest(
    feeId,
    `${Endpoints.FeeAgreement}/${Endpoints.FeeAgreementSendReminder.replace(':id', feeId)}`,
    'post',
    null,
    strings.feeAgreements.messages.reminderOk
  );
  return result;
};

/**
 * @param {number} feeId
 * @returns {Promise<OperationResult>}
 */
const validateByCoach = async feeId => {
  const result = await sendRequest(
    feeId,
    `${Endpoints.FeeAgreement}/${Endpoints.FeeAgreementValidationByCoach.replace(':id', feeId)}`,
    'post',
    null,
    strings.feeAgreements.messages.approvedOk
  );

  return result;
};

/**
 * @param {number} companyId
 * @param {*} data
 * @returns {Promise<OperationResult>}
 */
const createManagedServiceAgreement = async (companyId, data) => {
  const result = await sendRequest(
    companyId,
    `${Endpoints.Companies}/${companyId}/${Endpoints.FeeAgreement}`,
    'post',
    data,
    strings.feeAgreements.messages.createdOk
  );

  return result;
};

/**
 * @param {number} companyId
 * @param {*} data
 * @returns {Promise<OperationResult>}
 */
const createUnmanagedServiceAgreement = async (companyId, data) => {
  const result = await sendRequest(
    companyId,
    `${Endpoints.Companies}/${companyId}/${Endpoints.FeeAgreementUnmanaged}`,
    'post',
    data,
    strings.feeAgreements.messages.createdOk
  );

  return result;
};

/**
 * @param {number} feeId
 * @returns {Promise<OperationResult>}
 */
const validateByOps = async feeId => {
  const result = await sendRequest(
    feeId,
    `${Endpoints.FeeAgreement}/${Endpoints.FeeAgreementValidationByOperations.replace(
      ':id',
      feeId
    )}`,
    'post',
    null,
    strings.feeAgreements.messages.approvedOk
  );

  return result;
};

/**
 * @param {number} feeId
 * @param {*} data
 * @returns {Promise<OperationResult>}
 */
const createPreview = async (feeId, data) => {
  const result = await sendRequest(
    feeId,
    `${Endpoints.FeeAgreement}/${Endpoints.FeeAgreementCreateSignatureRequestPreview.replace(
      ':id',
      feeId
    )}`,
    'post',
    data,
    null
  );

  return result;
};

/**
 * @param {number} feeId
 * @param {*} data
 * @returns {Promise<OperationResult>}
 */
const sendRevalidationToCoach = async (feeId, data) => {
  const result = await sendRequest(
    feeId,
    `${Endpoints.FeeAgreement}/${Endpoints.FeeAgreementRevalidationToCoach.replace(':id', feeId)}`,
    'put',
    data,
    strings.feeAgreements.messages.submittedOk
  );

  return result;
};

/**
 * @param {number} feeId
 * @param {*} data
 * @returns {Promise<OperationResult>}
 */
const sendRevalidationToOps = async (feeId, data) => {
  const result = await sendRequest(
    feeId,
    `${Endpoints.FeeAgreement}/${Endpoints.FeeAgreementRevalidationToOperations.replace(
      ':id',
      feeId
    )}`,
    'put',
    data,
    strings.feeAgreements.messages.submittedOk
  );

  return result;
};

/**
 * @param {number} feeId
 * @param {*} data
 * @returns {Promise<OperationResult>}
 */
const declineByCoach = async (feeId, data) => {
  const result = await sendRequest(
    feeId,
    `${Endpoints.FeeAgreement}/${Endpoints.FeeAgreementDeclinationByCoach.replace(':id', feeId)}`,
    'post',
    data,
    strings.feeAgreements.messages.declinedOk
  );

  return result;
};

/**
 * @param {number} feeId
 * @param {*} data
 * @returns {Promise<OperationResult>}
 */
const declineByOps = async (feeId, data) => {
  const result = await sendRequest(
    feeId,
    `${Endpoints.FeeAgreement}/${Endpoints.FeeAgreementDeclinationByOperations.replace(
      ':id',
      feeId
    )}`,
    'post',
    data,
    strings.feeAgreements.messages.declinedOk
  );

  return result;
};

/**
 * @param {number} feeId
 * @param {*} data
 * @returns {Promise<OperationResult>}
 */
const validateByOpsForUnmanaged = async (feeId, data) => {
  const result = await sendRequest(
    feeId,
    `${Endpoints.FeeAgreement}/${Endpoints.FeeAgreementUnmanagedValidationByOperations.replace(
      ':id',
      feeId
    )}`,
    'post',
    data,
    strings.feeAgreements.messages.savedOk
  );

  return result;
};

/**
 * @param {number} feeId
 * @returns {Promise<OperationResult>}
 */
const voidServiceAgreement = async feeId => {
  const result = await sendRequest(
    feeId,
    `${Endpoints.FeeAgreement}/${feeId}/${Endpoints.FeeAgreementVoidContract}`,
    'put',
    null,
    strings.feeAgreements.messages.voidedOk
  );

  return result;
};

/**
 * @param {number} feeId
 * @returns {Promise<OperationResult>}
 */
const cancelServiceAgreementRequest = async feeId => {
  const result = await sendRequest(
    feeId,
    `${Endpoints.FeeAgreement}/${feeId}/${Endpoints.FeeAgreementCancelValidationRequest}`,
    'put',
    null,
    strings.feeAgreements.messages.canceledOk
  );

  return result;
};

/**
 * @param {number} feeId
 * @param {*} data
 * @returns {Promise<OperationResult>}
 */
const updateSignersEmail = async (feeId, data) => {
  const result = await sendRequest(
    feeId,
    `${Endpoints.FeeAgreement}/${Endpoints.FeeAgreementUpdateEmails.replace(':id', feeId)}`,
    'put',
    data,
    strings.feeAgreements.messages.emailsOk
  );

  return result;
};

/**
 * @param {number} feeId
 * @returns {Promise<OperationResult>}
 */
const switchSigningProvider = async feeId => {
  const result = await sendRequest(
    feeId,
    `${Endpoints.FeeAgreement}/${feeId}/${Endpoints.FeeAgreementSwitchProvider}`,
    'put',
    null,
    strings.feeAgreements.messages.swithServiceOk
  );

  return result;
};

/**
 * @param {number} feeId
 * @returns {Promise<OperationResult>}
 */
const reactivateServiceAgreement = async feeId => {
  const result = await sendRequest(
    feeId,
    `${Endpoints.FeeAgreement}/${feeId}/${Endpoints.FeeAgreementRestoreExpired}`,
    'put',
    null,
    strings.feeAgreements.messages.reactivatedOk
  );

  return result;
};

/**
 * @param {number} feeId
 * @param {string} url
 * @param {string} method
 * @param {*} [data]
 * @param {string} successMessage
 * @returns {Promise<OperationResult>}
 */
export const sendRequest = async (feeId, url, method, data = null, successMessage) => {
  const result = { success: false };
  try {
    const response = await API({ method, url, data });
    if (response?.status === 200 || response?.status === 201) {
      result.success = true;
      result.data = response.data;
      result.alert = {
        severity: 'success',
        body: successMessage,
        key: feeId,
        ...BASE_ALERT
      };
    }
  } catch (error) {
    result.alert = {
      severity: 'error',
      title: AlertTitle,
      body: getErrorMessage(error),
      autoHideDuration: 5000,
      key: feeId,
      ...BASE_ALERT
    };
  }
  return result;
};

const useFetchGuaranteeOptions = (shouldFetch = true) => {
  const [options, setOptions] = useState({});

  useEffect(() => {
    shouldFetch &&
      (async () => {
        const response = await API.get(Endpoints.GuaranteeDaysGrouped);
        response.status === 200 && setOptions(response.data);
      })();
  }, [shouldFetch]);

  return options;
};

const useFetchTemplates = (shouldFetch = true) => {
  const [templates, setTemplates] = useState(null);

  useEffect(() => {
    shouldFetch &&
      (async () => {
        const response = await API.get(
          `${Endpoints.FeeAgreement}/${Endpoints.FeeAgreementTemplates}`
        );
        response.status === 200 && setTemplates(response.data);
      })();
  }, [shouldFetch]);

  return templates;
};

const useHelloSign = ({ onSend, onTemplateCreated, onTemplateCanceled }) => {
  const dispatch = useDispatch();

  const helloSignConfig = {
    clientId: window.GPAC_ENV?.HELLO_SIGN_CLIENT_ID || process.env.REACT_APP_HELLO_SIGN_CLIENT_ID,
    mode: window.GPAC_ENV?.HELLO_SIGN_MODE || process.env.REACT_APP_HELLO_SIGN_MODE
  };
  const isHelloSignInProduction = helloSignConfig.mode === 'prod';
  const helloSignOpenConfig = {
    allowCancel: true,
    testMode: !isHelloSignInProduction,
    debug: !isHelloSignInProduction,
    skipDomainVerification: !isHelloSignInProduction
  };

  const helloSignClient = new HelloSign({
    clientId: helloSignConfig.clientId
  });

  helloSignClient.on('send', () => {
    dispatch(
      showAlert({
        severity: 'success',
        title: AlertTitle,
        body: strings.feeAgreements.messages.sentOk,
        autoHideDuration: 8000
      })
    );
    onSend && onSend();
  });

  helloSignClient.on('cancel', () => {
    dispatch(
      showAlert({
        severity: 'warning',
        title: AlertTitle,
        body: strings.feeAgreements.messages.templateCanceled
      })
    );
    onTemplateCanceled && onTemplateCanceled();
  });

  helloSignClient.on('createTemplate', templateData => {
    if (templateData) {
      dispatch(
        showAlert({
          severity: 'success',
          title: AlertTitle,
          body: strings.feeAgreements.messages.templateOk
        })
      );
      onTemplateCreated && onTemplateCreated(templateData);
    }
  });

  const handleTemplateUploaded = ({ edit_url }) =>
    helloSignClient.open(edit_url, helloSignOpenConfig);

  const handlePreviewCreated = ({ claim_url }) =>
    helloSignClient.open(claim_url, helloSignOpenConfig);

  return { helloSignClient, handleTemplateUploaded, handlePreviewCreated };
};

export {
  cancelServiceAgreementRequest,
  createManagedServiceAgreement,
  createPreview,
  createUnmanagedServiceAgreement,
  declineByCoach,
  declineByOps,
  reactivateServiceAgreement,
  sendReminder,
  sendRevalidationToCoach,
  sendRevalidationToOps,
  switchSigningProvider,
  updateSignersEmail,
  useFetchGuaranteeOptions,
  useFetchTemplates,
  useHelloSign,
  validateByCoach,
  validateByOps,
  validateByOpsForUnmanaged,
  voidServiceAgreement
};
